<template>
  <div
    ref="host"
    class="rete-flow-canvas"
    tabindex="0"
    aria-label="Canvas de edição de fluxo"
    @click="handleCanvasClick"
    @contextmenu="handleCanvasContextMenu"
  >
    <svg class="rete-flow-canvas__grid" aria-hidden="true" focusable="false">
      <defs>
        <pattern
          :id="gridPatternId"
          ref="gridPattern"
          width="24"
          height="24"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="2" cy="2" r="1.2" fill="#c8bfd6" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" :fill="`url(#${gridPatternId})`" />
    </svg>
    <div ref="area" class="rete-flow-canvas__area"></div>
    <div
      v-if="searchOpen"
      class="rete-flow-search"
      @pointerdown.stop
      @click.stop
      @contextmenu.stop
      @wheel.stop
    >
      <input
        ref="searchInput"
        :value="searchInputValue"
        type="text"
        placeholder="Buscar nos valores dos campos"
        aria-label="Buscar estágio"
        @input="aoDigitarBusca"
        @keydown.down.prevent="moverBusca(1)"
        @keydown.up.prevent="moverBusca(-1)"
        @keydown.enter.prevent="selecionarResultado(searchIndex)"
        @keydown.esc.prevent.stop="fecharBusca"
      />
      <ul v-if="searchResults.length" role="listbox">
        <li
          v-for="(item, index) in searchResults"
          :key="item.id"
          ref="searchItems"
          role="option"
          :aria-selected="index === searchIndex"
          :class="{ 'is-active': index === searchIndex }"
          @click="selecionarResultado(index)"
        >
          <span
            class="rete-flow-search__dot"
            :style="{ background: item.cor }"
          />
          <strong>{{ item.nome }}</strong>
          <small>#{{ item.id }}</small>
        </li>
      </ul>
      <p v-else-if="searchQuery.trim()">Nenhum estágio encontrado</p>
    </div>
  </div>
</template>

<script>
import { ClassicPreset, NodeEditor } from "rete";
import Vue from "vue";
import { AreaExtensions, AreaPlugin, Drag } from "rete-area-plugin";
import {
  ConnectionPlugin,
  Presets as ConnectionPresets
} from "rete-connection-plugin";
import { VuePlugin, Presets as VuePresets } from "rete-vue-plugin/vue2";
import { MinimapPlugin } from "rete-minimap-plugin";
import { HistoryPlugin, Presets as HistoryPresets } from "rete-history-plugin";
import {
  ArrangeAppliers,
  AutoArrangePlugin,
  Presets as ArrangePresets
} from "rete-auto-arrange-plugin";
import ReteFlowNode from "./ReteFlowNode.vue";
import ReteFlowConnection from "./ReteFlowConnection.vue";
import { reteInlineFields } from "./reteStageFields";

const socket = new ClassicPreset.Socket("Estágio");

function decisionGroups(estagio) {
  const fields = estagio?.ces_metadata?.campos || [];
  return Array.isArray(fields)
    ? fields.filter((group) => Array.isArray(group))
    : [];
}

function decisionLabel(group, index) {
  const getValue = (bindText) =>
    group?.find((field) => field.bindText === bindText)?.value;
  const condition = String(getValue("cec_condicao") || "").trim();
  if (condition) return `Se ${condition}`;
  const parameter = getValue("cec_parametro");
  const operator = getValue("cec_operador");
  const value = getValue("cec_valor");
  if (
    parameter &&
    operator &&
    value !== undefined &&
    value !== null &&
    value !== ""
  ) {
    return `Se {${parameter}} ${operator} ${value}`;
  }
  return `Condição ${index + 1}`;
}

function findField(group, bindText) {
  return group?.find((field) => field.bindText === bindText) || null;
}

function searchableStageFields(stage) {
  const entries = [];
  const valuesOf = (value) => {
    if (["string", "number", "boolean"].includes(typeof value))
      return [String(value)].filter((text) => text.trim());
    if (Array.isArray(value)) return value.flatMap(valuesOf);
    if (value && typeof value === "object")
      return Object.values(value).flatMap(valuesOf);
    return [];
  };
  const visit = (fields, groupLabel = "") => {
    if (!Array.isArray(fields)) return;
    fields.forEach((field, index) => {
      if (Array.isArray(field)) {
        visit(
          field,
          `${Number(stage.chatbot_estagio_id) === 10 ? "Condição" : "Grupo"} ${
            index + 1
          }`
        );
        return;
      }
      if (!field || typeof field !== "object" || !("value" in field)) return;
      let value = field.value;
      if (field.type === "json" && typeof value === "string") {
        try {
          value = JSON.parse(value);
        } catch {
          // Um JSON ainda em edição continua pesquisável como texto.
        }
      }
      entries.push({
        field,
        label: [groupLabel, field.label || "Campo"].filter(Boolean).join(" · "),
        values: valuesOf(value)
      });
    });
  };
  visit(stage.ces_metadata?.campos);
  return entries;
}

function textMatches(text, query) {
  const offsets = [];
  let normalized = "";
  for (let index = 0; index < text.length; ) {
    const character = String.fromCodePoint(text.codePointAt(index));
    const end = index + character.length;
    const normalizedCharacter = character
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();
    for (const part of normalizedCharacter) {
      normalized += part;
      for (let unit = 0; unit < part.length; unit += 1) {
        offsets.push({ start: index, end });
      }
    }
    index = end;
  }

  const needle = String(query || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
  if (!needle) return [];
  const matches = [];
  let fromIndex = 0;
  while (fromIndex < normalized.length) {
    const start = normalized.indexOf(needle, fromIndex);
    if (start < 0) break;
    matches.push({
      start: offsets[start].start,
      end: offsets[start + needle.length - 1].end
    });
    fromIndex = start + needle.length;
  }
  return matches;
}

function escapeHtml(text) {
  return text.replace(/[&<>"']/g, (character) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    };
    return entities[character];
  });
}

function highlightHtml(text, matches) {
  let result = "";
  let cursor = 0;
  matches.forEach(({ start, end }) => {
    result += `${escapeHtml(text.slice(cursor, start))}<mark>${escapeHtml(
      text.slice(start, end)
    )}</mark>`;
    cursor = end;
  });
  return result + escapeHtml(text.slice(cursor));
}

function decisionOutputs(estagio, outgoingLinks = []) {
  const groups = decisionGroups(estagio);
  const used = new Set();
  return {
    branches: outgoingLinks.map((link, index) => {
      const group =
        groups.find(
          (item) =>
            String(findField(item, "para_chatbot_fluxo_estagio_id")?.value) ===
            String(link.to)
        ) ||
        groups.find(
          (item) =>
            !used.has(item) &&
            findField(item, "para_chatbot_fluxo_estagio_id")?.value == null
        );
      if (group) used.add(group);
      return {
        key: `branch-to-${link.to}`,
        label: group ? decisionLabel(group, index) : `Condição ${index + 1}`,
        fields: group
          ? {
              parameter: findField(group, "cec_parametro"),
              operator: findField(group, "cec_operador"),
              value: findField(group, "cec_valor"),
              advanced: findField(group, "cec_avancado"),
              condition: findField(group, "cec_condicao")
            }
          : null
      };
    }),
    add: { key: "branch-new", label: "Nova condição" }
  };
}

class FluxoNode extends ClassicPreset.Node {
  constructor(estagio) {
    super(estagio.label || estagio.type || estagio.ces_nome || "Estágio");
    this.id = String(estagio.id);
    this.width = 300;
    this.height = 100;
    this.flowColor = estagio.cor || estagio.ces_metadata?.cor || "#462181";
    this.flowTitle =
      estagio.ces_nome || estagio.label || estagio.type || "Estágio";
    this.flowOrder = String(estagio.id);
    this.flowNodeId = estagio.id;
    this.flowStage = estagio;
    this.flowIsDecision = Number(estagio.chatbot_estagio_id) === 10;
    this.flowBranches = [];
    this.flowAddOutput = null;
    this.addInput("input", new ClassicPreset.Input(socket, "Entrada", true));
    this.configurarSaidas(estagio);
  }

  configurarSaidas(estagio, outgoingLinks = [], fieldOptions = {}) {
    this.flowInlineFields = reteInlineFields(estagio, fieldOptions);
    this.flowFieldOptions = fieldOptions;
    this.flowIsDecision = Number(estagio.chatbot_estagio_id) === 10;
    const definitions = this.flowIsDecision
      ? decisionOutputs(estagio, outgoingLinks)
      : { branches: [], add: null };
    this.flowBranches = definitions.branches;
    this.flowAddOutput = definitions.add;
    const outputs = this.flowIsDecision
      ? [...definitions.branches, definitions.add]
      : [{ key: "output", label: "Próximo estágio" }];
    const outputKeys = outputs.map((output) => output.key);
    Object.keys(this.outputs).forEach((key) => {
      if (!outputKeys.includes(key)) this.removeOutput(key);
    });
    outputs.forEach((output) => {
      const existing = this.outputs[output.key];
      if (existing) existing.label = output.label;
      else {
        this.addOutput(
          output.key,
          // O preset do Rete remove a conexão anterior antes de validar uma
          // nova quando multipleConnections é false. A regra de negócio é
          // validada no editor, então preserve o grafo até essa validação.
          new ClassicPreset.Output(socket, output.label, true)
        );
      }
    });
    this.height = this.flowIsDecision
      ? Math.max(116, 96 + definitions.branches.length * 65)
      : Math.max(
          100,
          100 +
            this.flowInlineFields.reduce(
              (height, entry) =>
                height +
                (entry.field.type === "bool"
                  ? 28
                  : ["textArea", "textarea"].includes(entry.field.type)
                  ? 74
                  : 54),
              0
            )
        );
  }
}

export default {
  name: "ReteFlowCanvas",
  props: {
    scene: { type: Object, required: true },
    ready: { type: Boolean, default: false },
    selectionMode: { type: Boolean, default: false },
    fieldOptions: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      gridPatternId: `rete-grid-${this._uid}`,
      editor: null,
      area: null,
      history: null,
      arrange: null,
      selector: null,
      selectable: null,
      syncingGraph: false,
      historyBusy: false,
      pendingNodeRemovals: new Set(),
      modifierActive: false,
      pickedAlreadySelected: false,
      highlightedLinkId: null,
      searchOpen: false,
      searchQuery: "",
      searchInputValue: "",
      searchTimer: null,
      stageSearchIndex: [],
      searchIndex: 0,
      nextLinkId: 1,
      destroyed: false
    };
  },
  computed: {
    searchResults() {
      const term = this.normalizarBusca(this.searchQuery);
      if (!term || !this.searchOpen) return [];
      return this.stageSearchIndex
        .filter((item) => item.textos.some((text) => text.includes(term)))
        .slice(0, 50);
    }
  },
  watch: {
    fieldOptions: {
      handler() {
        this.nodeSignatures.clear();
        this.agendarSync();
      },
      deep: true
    },
    ready(value) {
      if (!value) return;
      this.syncFromScene().then(() => {
        if (this.history) this.history.clear();
        this.centralizar();
      });
    },
    selectionMode() {
      this.atualizarModoSelecao();
    },
    scene: {
      handler() {
        this.agendarSync();
      },
      deep: true
    }
  },
  created() {
    // Não reativos: só otimizam a sincronização.
    this.nodeSignatures = new Map();
    this.nodeObservers = new Map();
    this.searchIndexTimer = null;
    this.syncTimer = null;
    this.syncPending = false;
    this.syncPromise = null;
    this.pointerStart = null;
    this.highlightFrame = null;
  },
  async mounted() {
    await this.inicializarEditor();
    document.addEventListener("keydown", this.aoTeclarDelete, true);
    this.atualizarGrade();
    if (this.ready) {
      await this.syncFromScene();
      this.history.clear();
      this.centralizar();
    }
  },
  beforeDestroy() {
    this.destroyed = true;
    this.nodeObservers.forEach((observer) => observer.disconnect());
    this.nodeObservers.clear();
    clearTimeout(this.searchTimer);
    clearTimeout(this.searchIndexTimer);
    clearTimeout(this.syncTimer);
    cancelAnimationFrame(this.highlightFrame);
    document.removeEventListener("keydown", this.aoTeclarBusca, true);
    document.removeEventListener("keydown", this.aoTeclarDelete, true);
    if (this.area) this.area.destroy();
  },
  methods: {
    handleCanvasContextMenu(event) {
      const connection = event.target.closest?.("[data-rete-connection-id]");
      if (connection) {
        event.preventDefault();
        event.stopPropagation();
        this.$emit("canvas-contextmenu", {
          clientX: event.clientX,
          clientY: event.clientY,
          position: this.positionFromEvent(event),
          connectionId: connection.dataset.reteConnectionId
        });
        return;
      }
      if (
        event.target.closest?.(".rete-stage-node, .rete-flow-connection") ||
        !this.$refs.area?.contains(event.target)
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      this.$emit("canvas-contextmenu", {
        clientX: event.clientX,
        clientY: event.clientY,
        position: this.positionFromEvent(event)
      });
    },
    atualizarGrade() {
      const pattern = this.$refs.gridPattern;
      const transform = this.area?.area.transform;
      if (
        !pattern ||
        !transform ||
        !Number.isFinite(transform.k) ||
        transform.k <= 0
      )
        return;
      let spacing = 24 * transform.k;
      while (spacing < 14) spacing *= 2;
      pattern.setAttribute("width", String(spacing));
      pattern.setAttribute("height", String(spacing));
      const x = Number.isFinite(transform.x) ? transform.x : 0;
      const y = Number.isFinite(transform.y) ? transform.y : 0;
      pattern.setAttribute("patternTransform", `translate(${x} ${y})`);
    },
    async abrirBusca() {
      this.searchOpen = true;
      this.reconstruirIndiceBusca();
      document.addEventListener("keydown", this.aoTeclarBusca, true);
      this.highlightedLinkId = null;
      await this.$nextTick();
      this.$refs.searchInput?.focus();
      this.aplicarDestaque();
    },
    aoTeclarBusca(event) {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      this.fecharBusca();
    },
    normalizarBusca(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
    },
    reconstruirIndiceBusca() {
      this.stageSearchIndex = (this.scene.nodes || []).map((stage) => {
        const campos = searchableStageFields(stage);
        return {
          id: String(stage.id),
          nome: stage.ces_nome || stage.label || stage.type || "Estágio",
          cor: stage.cor || stage.ces_metadata?.cor || "#462181",
          campos,
          textos: campos.flatMap((entry) =>
            entry.values.map((value) => this.normalizarBusca(value))
          )
        };
      });
    },
    aoDigitarBusca(event) {
      this.searchInputValue = event.target.value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchIndexDirty) {
          this.reconstruirIndiceBusca();
          this.searchIndexDirty = false;
        }
        this.searchQuery = this.searchInputValue;
        this.searchIndex = 0;
        this.onSearchChange();
      }, 250);
    },
    async selecionarResultado(index) {
      this.searchIndex = index;
      const item = this.searchResults[index];
      this.fecharBusca();
      if (item) await this.irParaResultado(item, 1.3);
    },
    fecharBusca() {
      clearTimeout(this.searchTimer);
      document.removeEventListener("keydown", this.aoTeclarBusca, true);
      this.searchOpen = false;
      this.searchIndexDirty = false;
      this.searchInputValue = "";
      this.searchQuery = "";
      this.searchIndex = 0;
      this.aplicarDestaque();
      this.$refs.host?.focus();
    },
    onSearchChange() {
      this.aplicarDestaque();
    },
    moverBusca(step) {
      const total = this.searchResults.length;
      if (!total) return;
      this.searchIndex = (this.searchIndex + step + total) % total;
      this.aplicarDestaque();
      this.irParaResultado();
      this.$nextTick(() =>
        this.$refs.searchItems?.[this.searchIndex]?.scrollIntoView({
          block: "nearest"
        })
      );
    },
    async irParaResultado(found, scale = 0.85) {
      const item = found || this.searchResults[this.searchIndex];
      const node = item && this.editor?.getNode(item.id);
      const view = node && this.area?.nodeViews.get(node.id);
      if (!view) return;
      const bounds = this.$refs.area.getBoundingClientRect();
      await this.area.area.zoom(scale, 0, 0);
      const k = this.area.area.transform.k;
      const x = bounds.width / 2 - (view.position.x + node.width / 2) * k;
      const y = bounds.height / 2 - (view.position.y + node.height / 2) * k;
      await this.area.area.translate(x, y);
    },
    fluxoDestacado() {
      if (this.searchOpen && this.searchQuery.trim()) {
        const current = this.searchResults[this.searchIndex]?.id;
        return {
          nodes: new Set(this.searchResults.map((item) => item.id)),
          links: new Set(),
          current,
          searchMatch: true
        };
      }
      const link = (this.scene.links || []).find(
        (item) => String(item.id) === String(this.highlightedLinkId)
      );
      if (!link) return null;
      const nodes = new Set([String(link.from)]);
      const links = new Set([String(link.id)]);
      const visited = new Set();
      const queue = [String(link.to)];
      while (queue.length) {
        const id = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);
        nodes.add(id);
        for (const item of this.scene.links) {
          if (String(item.from) !== id) continue;
          links.add(String(item.id));
          queue.push(String(item.to));
        }
      }
      return { nodes, links };
    },
    mostrarResultadosOcultos(node, element, term) {
      const indexed = this.stageSearchIndex.find(
        (entry) => entry.id === String(node.flowNodeId ?? node.id)
      );
      const controls = [
        ...element.querySelectorAll(".rete-inline-fields__field")
      ];
      const hidden = (indexed?.campos || []).flatMap((entry) => {
        const matching = entry.values.filter(
          (value) => textMatches(value, term).length
        );
        if (!matching.length) return [];
        const visible = controls.filter(
          (control) => control.dataset.reteField === entry.field.bindText
        );
        return matching
          .filter(
            (value) =>
              !visible.some((control) => {
                const texts = [
                  ...control.querySelectorAll(
                    "input[type='text'], textarea, .p-dropdown-label, .p-multiselect-label"
                  )
                ].map((input) =>
                  input.matches("input, textarea")
                    ? input.value
                    : input.textContent
                );
                return texts.some(
                  (text) =>
                    text.includes(value) && textMatches(text, term).length
                );
              })
          )
          .map((value) => ({ label: entry.label, value }));
      });
      if (!hidden.length) return;
      const details = document.createElement("div");
      details.className = "rete-flow-search-details";
      const heading = document.createElement("strong");
      heading.textContent = "Encontrado em outros campos";
      details.appendChild(heading);
      hidden.slice(0, 3).forEach(({ label, value }) => {
        const row = document.createElement("div");
        const name = document.createElement("small");
        name.textContent = label;
        const content = document.createElement("span");
        const first = textMatches(value, term)[0];
        const start = Math.max(0, first.start - 45);
        const end = Math.min(value.length, first.end + 65);
        const excerpt = `${start ? "…" : ""}${value.slice(start, end)}${
          end < value.length ? "…" : ""
        }`;
        content.innerHTML = highlightHtml(excerpt, textMatches(excerpt, term));
        row.append(name, content);
        details.appendChild(row);
      });
      if (hidden.length > 3) {
        const more = document.createElement("small");
        more.textContent = `Mais ${
          hidden.length - 3
        } resultados nas opções do estágio`;
        details.appendChild(more);
      }
      element.querySelector(".rete-stage-node")?.appendChild(details);
    },
    aplicarDestaque() {
      if (!this.editor || !this.area) return;
      const flow = this.fluxoDestacado();
      if (!flow) this.highlightedLinkId = null;
      const ranges = [];
      const mark = (element, active) => {
        if (!element) return;
        element.classList.toggle("rete-flow--dim", Boolean(flow) && !active);
        element.classList.toggle("rete-flow--active", Boolean(flow) && active);
      };
      const searchTerm = flow?.searchMatch ? this.searchQuery.trim() : "";
      for (const node of this.editor.getNodes()) {
        const element = this.area.nodeViews.get(node.id)?.element;
        const isMatch =
          Boolean(flow?.searchMatch) &&
          flow?.nodes.has(String(node.flowNodeId ?? node.id));
        element
          ?.querySelectorAll(
            ".rete-flow-search-text-overlay, .rete-flow-search-details"
          )
          .forEach((overlay) => overlay.remove());
        element
          ?.querySelectorAll(".rete-inline-fields__field--search-match")
          .forEach((field) =>
            field.classList.remove("rete-inline-fields__field--search-match")
          );
        element?.classList.toggle(
          "rete-flow--current",
          Boolean(flow?.current) &&
            String(node.flowNodeId ?? node.id) === flow.current
        );
        element?.classList.toggle("rete-flow--search-match", isMatch);
        if (isMatch && searchTerm && element) {
          const matchingFields = new Set(
            (
              this.stageSearchIndex.find(
                (entry) => entry.id === String(node.flowNodeId ?? node.id)
              )?.campos || []
            )
              .filter((entry) =>
                entry.values.some(
                  (value) => textMatches(value, searchTerm).length
                )
              )
              .map((entry) => entry.field.bindText)
          );
          const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT
          );
          let textNode;
          while ((textNode = walker.nextNode())) {
            const parent = textNode.parentElement;
            if (!parent?.closest(".p-dropdown-label, .p-multiselect-label"))
              continue;
            if (
              !matchingFields.has(
                parent.closest(".rete-inline-fields__field")?.dataset.reteField
              )
            )
              continue;
            textMatches(textNode.textContent, searchTerm).forEach(
              ({ start, end }) => {
                const range = document.createRange();
                range.setStart(textNode, start);
                range.setEnd(textNode, end);
                ranges.push(range);
              }
            );
          }
          element
            .querySelectorAll("input[type='text'], textarea")
            .forEach((input) => {
              const value = input.value || "";
              const matches = textMatches(value, searchTerm);
              if (!matches.length) return;
              const field = input.closest(".rete-inline-fields__field");
              if (!field) return;
              field.classList.add("rete-inline-fields__field--search-match");
              const style = window.getComputedStyle(input);
              const overlay = document.createElement("div");
              overlay.className = "rete-flow-search-text-overlay";
              overlay.setAttribute("aria-hidden", "true");
              overlay.innerHTML = highlightHtml(value, matches);
              Object.assign(overlay.style, {
                left: `${input.offsetLeft - input.scrollLeft}px`,
                top: `${input.offsetTop - input.scrollTop}px`,
                width: `${input.offsetWidth}px`,
                height: `${input.offsetHeight}px`,
                font: style.font,
                lineHeight: style.lineHeight,
                letterSpacing: style.letterSpacing,
                textAlign: style.textAlign,
                padding: style.padding,
                border: "1px solid transparent",
                boxSizing: "border-box",
                whiteSpace: input.tagName === "TEXTAREA" ? "pre-wrap" : "pre",
                overflow: "hidden",
                wordBreak:
                  input.tagName === "TEXTAREA" ? "break-word" : "normal"
              });
              field.appendChild(overlay);
            });
          this.mostrarResultadosOcultos(node, element, searchTerm);
        }
        mark(element, flow?.nodes.has(String(node.flowNodeId ?? node.id)));
      }
      if (window.CSS?.highlights && window.Highlight) {
        if (ranges.length) {
          window.CSS.highlights.set(
            "rete-flow-search-match",
            new window.Highlight(...ranges)
          );
        } else {
          window.CSS.highlights.delete("rete-flow-search-match");
        }
      }
      for (const connection of this.editor.getConnections()) {
        mark(
          this.area.connectionViews.get(connection.id)?.element,
          flow?.links.has(String(connection.flowLinkId ?? connection.id))
        );
      }
    },
    async handleCanvasClick(event) {
      if (this.searchOpen && !event.target.closest?.(".rete-flow-search")) {
        this.fecharBusca();
      }
      const connection = event.target.closest?.("[data-rete-connection-id]");
      if (connection) {
        event.preventDefault();
        event.stopPropagation();
        this.highlightedLinkId = connection.dataset.reteConnectionId;
        this.aplicarDestaque();
        return;
      }
      const addConnectedButton = event.target.closest?.(
        ".rete-stage-node__add-connected"
      );
      if (addConnectedButton) {
        event.preventDefault();
        event.stopPropagation();
        const node = this.editor?.getNode(addConnectedButton.dataset.nodeId);
        if (node) this.$emit("add-connected-stage", node.flowNodeId);
        return;
      }
      const button = event.target.closest?.(".rete-stage-node__options");
      if (!button || !this.editor) return;
      event.preventDefault();
      event.stopPropagation();
      const node = this.editor.getNode(button.dataset.nodeId);
      if (!node) return;
      if (this.selector) await this.selector.unselectAll();
      if (this.selectable) await this.selectable.select(node.id, true);
      this.$emit("node-options", node.flowNodeId);
    },
    atualizarModoSelecao() {
      const area = this.area?.area;
      if (!area) return;
      area.setDragHandler(this.selectionMode ? null : new Drag());
    },
    async inicializarEditor() {
      const editor = new NodeEditor();
      const area = new AreaPlugin(this.$refs.area);
      const connections = new ConnectionPlugin();
      const render = new VuePlugin({ setup: (context) => new Vue(context) });
      const history = new HistoryPlugin({ timing: 350 });
      const minimap = new MinimapPlugin({ ratio: 0.16, minDistance: 1000 });
      const arrange = new AutoArrangePlugin();

      connections.addPreset(ConnectionPresets.classic.setup());
      render.addPreset(
        VuePresets.classic.setup({
          customize: {
            node: () => ReteFlowNode,
            connection: () => ReteFlowConnection
          }
        })
      );
      render.addPreset(VuePresets.minimap.setup({ size: 175 }));
      history.addPreset(HistoryPresets.classic.setup({ timing: 350 }));
      arrange.addPreset(ArrangePresets.classic.setup());

      this.editor = editor;
      this.area = area;
      this.history = history;
      this.arrange = arrange;

      editor.use(area);
      area.use(connections);
      area.use(render);
      area.use(history);
      area.use(minimap);
      area.use(arrange);
      this.atualizarModoSelecao();
      this.selector = AreaExtensions.selector();
      editor.addPipe(async (context) => {
        if (this.syncingGraph || !context?.type) return context;
        if (context.type === "connectioncreate") {
          const source = editor.getNode(context.data.source);
          const target = editor.getNode(context.data.target);
          const erro = this.validarNovaLigacao(
            source?.flowNodeId,
            target?.flowNodeId,
            context.data.sourceOutput
          );
          if (erro) {
            this.$emit("connection-rejected", erro);
            return undefined;
          }
        }
        if (context.type === "nodecreated") {
          const stage = context.data.flowStage;
          if (stage && !this.obterNodeDoFluxo(stage.id)) {
            const index =
              context.data.flowStageIndex ?? this.scene.nodes.length;
            this.scene.nodes.splice(index, 0, stage);
            this.$emit("scene-change", this.scene);
          }
        }
        if (context.type === "noderemove") {
          const id = context.data.id;
          context.data.flowStageIndex = this.scene.nodes.findIndex(
            (stage) => String(stage.id) === String(context.data.flowNodeId)
          );
          this.pendingNodeRemovals.add(id);
          try {
            const attached = editor
              .getConnections()
              .filter(
                (connection) =>
                  connection.source === id || connection.target === id
              );
            for (const connection of attached) {
              if (!(await editor.removeConnection(connection.id))) {
                this.pendingNodeRemovals.delete(id);
                this.$nextTick(() => this.syncFromScene());
                return;
              }
            }
          } catch (error) {
            this.pendingNodeRemovals.delete(id);
            this.$nextTick(() => this.syncFromScene());
            throw error;
          }
        }
        if (context.type === "connectioncreated") {
          const connection = context.data;
          const source = editor.getNode(connection.source);
          const target = editor.getNode(connection.target);
          if (!source || !target) return context;
          if (connection.flowDecisionGroup) {
            const { group, index } = connection.flowDecisionGroup;
            const fields = this.obterNodeDoFluxo(source.flowNodeId)
              ?.ces_metadata?.campos;
            if (Array.isArray(fields) && !fields.includes(group))
              fields.splice(index, 0, group);
          }
          const link = {
            id: connection.flowLinkId ?? this.nextLinkId++,
            from: source.flowNodeId,
            to: target.flowNodeId
          };
          connection.flowLinkId = link.id;
          this.scene.links.push(link);
          this.$emit("connection-added", link, connection.sourceOutput);
          this.$emit("scene-change", this.scene);
        }
        if (context.type === "connectionremoved") {
          const connection = context.data;
          const link = this.scene.links.find(
            (item) =>
              String(item.id) === String(connection.flowLinkId ?? connection.id)
          );
          if (link) {
            this.removerCondicaoDaConexao(connection, link);
            this.$emit("connection-removed", link);
            this.scene.links = this.scene.links.filter((item) => item !== link);
            this.$emit("scene-change", this.scene);
          }
        }
        if (context.type === "noderemoved") {
          const node = context.data;
          this.scene.nodes = this.scene.nodes.filter(
            (item) => String(item.id) !== String(node.flowNodeId ?? node.id)
          );
          this.$emit("scene-change", this.scene);
          this.emitirSelecao();
          this.pendingNodeRemovals.delete(node.id);
          this.$nextTick(() => this.syncFromScene());
        }
        return context;
      });
      area.addPipe((context) => {
        if (context?.type === "pointerdown") {
          const event = context.data.event;
          this.pointerStart = { x: event.clientX, y: event.clientY };
          this.pickedAlreadySelected = false;
          this.modifierActive = Boolean(
            event.shiftKey || event.ctrlKey || event.metaKey
          );
        }
        if (context?.type === "nodepicked") {
          // Pegar um estágio já selecionado preserva o grupo para arrastar junto.
          this.pickedAlreadySelected = Boolean(
            this.editor?.getNode(context.data.id)?.selected
          );
          this.$nextTick(() => this.emitirSelecao(context.data.id));
        }
        if (context?.type === "nodetranslated") {
          this.registrarPosicao(context.data.id, context.data.position);
          this.$emit("node-moved", context.data.id);
        }
        if (["translated", "zoomed"].includes(context?.type)) {
          this.atualizarGrade();
          this.$emit("viewport-change");
        }
        if (context?.type === "pointerup") {
          const target = context.data.event.target;
          const pointerUpInsideCanvas = this.$refs.area?.contains(target);
          // AreaPlugin escuta pointerup no window, inclusive dos menus no body.
          // Interrompa também os pipes seguintes (como selectableNodes).
          if (!pointerUpInsideCanvas) return;
          const upEvent = context.data.event;
          const dragged =
            this.pointerStart &&
            Math.hypot(
              upEvent.clientX - this.pointerStart.x,
              upEvent.clientY - this.pointerStart.y
            ) > 5;
          if (!target.closest?.(".rete-stage-node")) {
            this.selector.unselectAll();
            if (
              !dragged &&
              this.highlightedLinkId &&
              !target.closest?.("[data-rete-connection-id]")
            ) {
              this.highlightedLinkId = null;
              this.aplicarDestaque();
            }
            this.$emit("canvas-click");
          }
          this.$nextTick(() => this.emitirSelecao());
        }
        if (
          context?.type === "rendered" &&
          context.data.type === "node" &&
          typeof ResizeObserver !== "undefined"
        ) {
          this.observarAlturaNode(
            context.data.payload.id,
            context.data.element
          );
        }
        if (["rendered", "unmounted"].includes(context?.type)) {
          this.agendarDestaque();
        }
        return context;
      });
      this.selectable = AreaExtensions.selectableNodes(area, this.selector, {
        accumulating: {
          active: () => this.modifierActive || this.pickedAlreadySelected
        }
      });
    },
    // O card cresce com o conteúdo (ex.: textarea redimensionado); avisa o Rete
    // para reposicionar os sockets e as ligações.
    observarAlturaNode(id, element) {
      this.nodeObservers.get(id)?.disconnect();
      const card = element.querySelector(".rete-stage-node") || element;
      const observer = new ResizeObserver(() => {
        const node = this.editor?.getNode(id);
        const height = card.offsetHeight;
        if (!node || !height || Math.abs(height - node.height) < 1) return;
        node.height = height;
        this.area?.resize(id, node.width, height);
      });
      observer.observe(card);
      this.nodeObservers.set(id, observer);
    },
    obterNodeDoFluxo(id) {
      return this.scene.nodes.find((node) => String(node.id) === String(id));
    },
    validarNovaLigacao(fromId, toId, sourceOutput) {
      if (String(fromId) === String(toId))
        return "Um estágio não pode ser conectado a ele mesmo.";
      const erroDaSaida = this.validarNovaSaida(fromId);
      if (erroDaSaida) return erroDaSaida;
      const origem = this.obterNodeDoFluxo(fromId);
      const saidaJaConectada = (this.editor?.getConnections() || []).some(
        (connection) =>
          String(connection.source) === String(fromId) &&
          connection.sourceOutput === sourceOutput
      );
      if (
        Number(origem?.chatbot_estagio_id) === 10 &&
        sourceOutput &&
        (saidaJaConectada ||
          (this.scene.links || []).some(
            (link) =>
              String(link.from) === String(fromId) &&
              this.outputForLink(link) === sourceOutput
          ))
      )
        return "Esta condição já tem um estágio conectado. Use o + para criar outra condição.";
      const jaTemPai = (this.scene.links || []).some(
        (link) => String(link.to) === String(toId)
      );
      if (jaTemPai)
        return "Este estágio já possui um estágio anterior conectado.";
      return null;
    },
    validarNovaSaida(fromId) {
      const origem = this.obterNodeDoFluxo(fromId);
      if (!origem) return "Não foi possível localizar o estágio de origem.";
      const quantidadeFilhos = (this.scene.links || []).filter(
        (link) => String(link.from) === String(fromId)
      ).length;
      const ehDecisao = Number(origem.chatbot_estagio_id) === 10;
      const limiteConfigurado = Number(origem.ces_metadata?.qtd_max_filho);
      const limite = ehDecisao
        ? limiteConfigurado
        : Math.min(
            Number.isFinite(limiteConfigurado) ? limiteConfigurado : 1,
            1
          );

      if (quantidadeFilhos >= limite) {
        if (!ehDecisao && quantidadeFilhos >= 1)
          return "Um estágio só pode ter um filho. Apenas Tomar Decisão pode ter mais de um.";
        return "Este estágio não permite adicionar outro estágio filho.";
      }
      return null;
    },
    getNodeElement(id) {
      const node = this.editor
        ?.getNodes()
        .find((item) => String(item.flowNodeId) === String(id));
      return node ? this.area?.nodeViews.get(node.id)?.element || null : null;
    },
    criarNode(estagio) {
      return new FluxoNode(estagio);
    },
    criarConexao(link) {
      const origem = this.editor.getNode(String(link.from));
      const destino = this.editor.getNode(String(link.to));
      if (!origem || !destino) return null;
      const conexao = new ClassicPreset.Connection(
        origem,
        this.outputForLink(link),
        destino,
        "input"
      );
      conexao.id = String(link.id);
      conexao.flowLinkId = link.id;
      return conexao;
    },
    outputForLink(link) {
      const origem = this.obterNodeDoFluxo(link.from);
      if (Number(origem?.chatbot_estagio_id) !== 10) return "output";
      return `branch-to-${link.to}`;
    },
    removerCondicaoDaConexao(connection, link) {
      const stage = this.obterNodeDoFluxo(link.from);
      if (Number(stage?.chatbot_estagio_id) !== 10) return;
      const fields = stage.ces_metadata?.campos;
      if (!Array.isArray(fields)) return;
      let index = fields.findIndex(
        (group) =>
          Array.isArray(group) &&
          String(findField(group, "para_chatbot_fluxo_estagio_id")?.value) ===
            String(link.to)
      );
      if (index < 0) {
        const outgoing = this.scene.links.filter(
          (item) => String(item.from) === String(link.from)
        );
        const group = decisionGroups(stage)[outgoing.indexOf(link)];
        index = group ? fields.indexOf(group) : -1;
      }
      if (index >= 0) {
        connection.flowDecisionGroup = { group: fields[index], index };
        fields.splice(index, 1);
      }
    },
    // Agrupa várias mudanças seguidas (arrastar, digitar) em uma sincronização.
    agendarSync() {
      if (this.searchOpen) {
        this.searchIndexDirty = true;
        clearTimeout(this.searchIndexTimer);
        this.searchIndexTimer = setTimeout(() => {
          if (!this.searchOpen || !this.searchIndexDirty) return;
          this.reconstruirIndiceBusca();
          this.searchIndexDirty = false;
          this.onSearchChange();
        }, 300);
      }
      if (!this.ready || !this.editor) return;
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.syncFromScene(), 60);
    },
    agendarDestaque() {
      if (!this.highlightedLinkId) return;
      cancelAnimationFrame(this.highlightFrame);
      this.highlightFrame = requestAnimationFrame(() => this.aplicarDestaque());
    },
    assinaturaNode(stage, outgoingLinks) {
      return JSON.stringify(
        [stage, outgoingLinks.map((link) => [link.id, link.to])],
        (key, value) => (key === "x" || key === "y" ? undefined : value)
      );
    },
    async syncFromScene() {
      if (this.historyBusy) return;
      if (this.syncPromise) {
        this.syncPending = true;
        return this.syncPromise;
      }
      this.syncPromise = this.sincronizarGrafo();
      try {
        await this.syncPromise;
      } finally {
        this.syncPromise = null;
      }
    },
    async sincronizarGrafo() {
      if (
        !this.editor ||
        !this.area ||
        !this.ready ||
        this.destroyed ||
        this.historyBusy ||
        this.pendingNodeRemovals.size
      )
        return;
      if (this.syncingGraph) {
        this.syncPending = true;
        return;
      }
      this.syncingGraph = true;
      try {
        const nodes = [...(this.scene.nodes || [])];
        const links = [...(this.scene.links || [])];
        const linksById = new Map(links.map((link) => [String(link.id), link]));
        const sceneNodeIds = new Set(nodes.map((node) => String(node.id)));
        const sceneLinkIds = new Set(links.map((link) => String(link.id)));

        for (const connection of this.editor.getConnections()) {
          if (
            !sceneLinkIds.has(String(connection.flowLinkId ?? connection.id))
          ) {
            await this.editor.removeConnection(connection.id);
          }
        }
        for (const node of this.editor.getNodes()) {
          if (!sceneNodeIds.has(String(node.flowNodeId ?? node.id))) {
            await this.editor.removeNode(node.id);
          }
        }

        const outgoingByNode = new Map();
        for (const link of links) {
          const key = String(link.from);
          if (!outgoingByNode.has(key)) outgoingByNode.set(key, []);
          outgoingByNode.get(key).push(link);
        }
        for (const id of [...this.nodeSignatures.keys()]) {
          if (!sceneNodeIds.has(id)) this.nodeSignatures.delete(id);
        }

        for (const stage of nodes) {
          const outgoingLinks = outgoingByNode.get(String(stage.id)) || [];
          const signature = this.assinaturaNode(stage, outgoingLinks);
          const unchanged =
            this.nodeSignatures.get(String(stage.id)) === signature;
          this.nodeSignatures.set(String(stage.id), signature);
          let reteNode = this.editor.getNode(String(stage.id));
          if (reteNode) reteNode.flowStage = stage;
          if (!reteNode) {
            reteNode = this.criarNode(stage);
            reteNode.configurarSaidas(stage, outgoingLinks, this.fieldOptions);
            await this.editor.addNode(reteNode);
            await this.area.translate(reteNode.id, {
              x: Number(stage.x) || 0,
              y: Number(stage.y) || 0
            });
          } else if (unchanged) {
            const view = this.area.nodeViews.get(reteNode.id);
            if (
              view &&
              (view.position.x !== Number(stage.x) ||
                view.position.y !== Number(stage.y))
            ) {
              await this.area.translate(reteNode.id, {
                x: Number(stage.x) || 0,
                y: Number(stage.y) || 0
              });
            }
            continue;
          } else {
            const heightBeforeUpdate = reteNode.height;
            reteNode.label =
              stage.label || stage.type || stage.ces_nome || "Estágio";
            reteNode.flowTitle =
              stage.ces_nome || stage.label || stage.type || "Estágio";
            reteNode.flowColor =
              stage.cor || stage.ces_metadata?.cor || "#462181";
            reteNode.flowOrder = String(stage.id);
            reteNode.configurarSaidas(stage, outgoingLinks, this.fieldOptions);
            if (
              !reteNode.flowIsDecision &&
              heightBeforeUpdate !== reteNode.height
            ) {
              await this.area.resize(
                reteNode.id,
                reteNode.width,
                reteNode.height
              );
            }
            await this.area.update("node", reteNode.id);
            const view = this.area.nodeViews.get(reteNode.id);
            if (
              view &&
              (view.position.x !== Number(stage.x) ||
                view.position.y !== Number(stage.y))
            ) {
              await this.area.translate(reteNode.id, {
                x: Number(stage.x) || 0,
                y: Number(stage.y) || 0
              });
            }
          }
          if (reteNode.flowIsDecision) {
            await this.$nextTick();
            const element = this.getNodeElement(stage.id)?.querySelector(
              ".rete-stage-node"
            );
            const height = element?.offsetHeight;
            if (height && height !== reteNode.height) {
              reteNode.height = height;
              await this.area.resize(reteNode.id, reteNode.width, height);
            }
          }
        }

        // Uma ligação recém-criada sai do "+". Depois da criação da condição,
        // mova-a para a saída estável do destino e refaça os observadores do socket.
        const connections = this.editor.getConnections();
        const connectionIds = new Set(
          connections.map((connection) =>
            String(connection.flowLinkId ?? connection.id)
          )
        );
        for (const connection of connections) {
          const link = linksById.get(
            String(connection.flowLinkId ?? connection.id)
          );
          if (!link) continue;
          const output = this.outputForLink(link);
          if (connection.sourceOutput === output) continue;
          connection.sourceOutput = output;
          const view = this.area.connectionViews.get(connection.id);
          if (view) {
            await this.area.emit({
              type: "unmount",
              data: { element: view.element }
            });
            await this.area.update("connection", connection.id);
          }
        }

        for (const link of links) {
          const connectionId = String(link.id);
          if (connectionIds.has(connectionId)) continue;
          const connection = this.criarConexao(link);
          if (connection) {
            await this.editor.addConnection(connection);
            connectionIds.add(connectionId);
          }
        }
        const numericIds = links
          .map((link) => Number(link.id))
          .filter(Number.isFinite);
        this.nextLinkId = Math.max(
          this.nextLinkId,
          Math.max(0, ...numericIds) + 1
        );
      } finally {
        this.syncingGraph = false;
        this.aplicarDestaque();
        if (this.syncPending) {
          this.syncPending = false;
          this.agendarSync();
        }
      }
    },
    registrarPosicao(reteId, position) {
      if (this.syncingGraph) return;
      const node = this.obterNodeDoFluxo(reteId);
      if (!node) return;
      node.x = position.x;
      node.y = position.y;
      this.$emit("scene-change", this.scene);
    },
    async removerConexao(connectionId) {
      const connection = this.editor
        .getConnections()
        .find((item) => String(item.id) === String(connectionId));
      if (!connection) return;
      await this.editor.removeConnection(connection.id);
    },
    async conectarEstagiosRete(fromId, toId) {
      const origem = this.obterNodeDoFluxo(fromId);
      const destino = this.obterNodeDoFluxo(toId);
      if (!origem || !destino) {
        throw new Error("Não foi possível preparar a conexão do estágio.");
      }
      const isDecision = Number(origem.chatbot_estagio_id) === 10;
      const outputKey = isDecision ? "branch-new" : "output";
      const erro = this.validarNovaLigacao(origem.id, destino.id, outputKey);
      if (erro) {
        this.$emit("connection-rejected", erro);
        return false;
      }
      // O botão pode ser usado enquanto o grafo está sincronizando. Nesse
      // intervalo, connectioncreated é ignorado: registre primeiro no fluxo
      // para que o próximo sync preserve a ligação e prepare a saída estável.
      const numericIds = this.scene.links
        .map((link) => Number(link.id))
        .filter(Number.isFinite);
      const id = Math.max(this.nextLinkId, Math.max(0, ...numericIds) + 1);
      this.nextLinkId = id + 1;
      const link = {
        id,
        from: origem.id,
        to: destino.id
      };
      this.scene.links.push(link);
      this.$emit("connection-added", link, outputKey);
      this.$emit("scene-change", this.scene);
      await this.$nextTick();
      await this.syncFromScene();
    },
    emitirSelecao(idClicado) {
      const selecionados = this.editor
        .getNodes()
        .filter((node) => node.selected)
        .map((node) => node.flowNodeId);
      this.$emit("selection-change", selecionados, idClicado);
    },
    // Compatibilidade com callbacks já registrados pelo editor antes do hot reload.
    emitSelection(idClicado) {
      this.emitirSelecao(idClicado);
    },
    async selectNodesInRect(rect, additive = false) {
      if (!this.editor || !this.area || !this.selectable) return;
      if (!additive) await this.selector.unselectAll();
      for (const node of this.editor.getNodes()) {
        const element = this.area.nodeViews.get(node.id)?.element;
        if (!element) continue;
        const bounds = element.getBoundingClientRect();
        const overlaps =
          bounds.left < rect.right &&
          bounds.right > rect.left &&
          bounds.top < rect.bottom &&
          bounds.bottom > rect.top;
        if (overlaps) await this.selectable.select(node.id, true);
      }
      this.emitirSelecao();
    },
    async clearSelection() {
      if (this.selector) await this.selector.unselectAll();
      this.$emit("selection-change", []);
    },
    async removerSelecionados() {
      const selecionados = this.editor
        .getNodes()
        .filter((node) => node.selected);
      try {
        for (const node of selecionados) {
          const removed = await this.editor.removeNode(node.id);
          if (!removed) throw new Error(`Não foi possível remover ${node.id}.`);
        }
      } catch (error) {
        this.$emit("delete-error", error);
        return;
      }
      this.$emit("selection-change", []);
    },
    aoTeclarDelete(event) {
      if (
        !["Delete", "Backspace"].includes(event.key) ||
        this.searchOpen ||
        event.target.closest?.(
          "input, textarea, select, [contenteditable='true'], .p-dropdown, .p-multiselect"
        )
      )
        return;
      const hasSelection = this.editor
        ?.getNodes()
        .some((node) => node.selected);
      if (!hasSelection) return;
      event.preventDefault();
      event.stopPropagation();
      this.removerSelecionados();
    },
    positionFromEvent(event) {
      if (!this.area) return { x: 0, y: 0 };
      this.area.area.setPointerFrom(event);
      return { ...this.area.area.pointer };
    },
    centerPosition() {
      if (!this.area) return { x: 0, y: 0 };
      const bounds = this.$refs.area.getBoundingClientRect();
      const transform = this.area.area.transform;
      return {
        x: (bounds.width / 2 - transform.x) / transform.k,
        y: (bounds.height / 2 - transform.y) / transform.k
      };
    },
    async centralizar() {
      if (!this.area) return;
      const nodes = this.editor.getNodes();
      if (nodes.length) await AreaExtensions.zoomAt(this.area, nodes);
      else await this.area.area.translate(0, 0);
    },
    async zoomIn() {
      if (!this.area) return;
      const bounds = this.$refs.area.getBoundingClientRect();
      const scale = Math.min(2, this.area.area.transform.k + 0.1);
      await this.area.area.zoom(scale, bounds.width / 2, bounds.height / 2);
    },
    async zoomOut() {
      if (!this.area) return;
      const bounds = this.$refs.area.getBoundingClientRect();
      const scale = Math.max(0.5, this.area.area.transform.k - 0.1);
      await this.area.area.zoom(scale, bounds.width / 2, bounds.height / 2);
    },
    async desfazer() {
      await this.aplicarHistorico("undo");
    },
    async refazer() {
      await this.aplicarHistorico("redo");
    },
    async aplicarHistorico(action) {
      if (!this.history || this.historyBusy) return;
      this.historyBusy = true;
      try {
        if (this.syncPromise) await this.syncPromise;
        await this.history[action]();
      } finally {
        this.historyBusy = false;
        await this.syncFromScene();
      }
    },
    async deslocar(x, y) {
      if (!this.area) return;
      const transform = this.area.area.transform;
      await this.area.area.translate(transform.x + x, transform.y + y);
    },
    async formatar() {
      if (!this.arrange || !this.editor.getNodes().length) return;
      await this.arrange.layout({
        options: {
          "elk.algorithm": "layered",
          "elk.direction": "RIGHT",
          "elk.spacing.nodeNode": "55",
          "elk.layered.spacing.nodeNodeBetweenLayers": "90"
        },
        applier: new ArrangeAppliers.TransitionApplier({ duration: 250 })
      });
      await this.centralizar();
    }
  }
};
</script>

<style lang="scss">
.rete-flow-canvas {
  user-select: none;
  -webkit-user-select: none;
  background-color: #faf9fc;
  height: 100%;
  min-height: 0;
  outline: none;
  overflow: hidden;
  position: relative;
  width: 100%;
}
.rete-flow-canvas > .rete-flow-canvas__grid {
  background: transparent;
  height: 100%;
  inset: 0;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 0;
}
.rete-flow-canvas__area {
  position: relative;
  z-index: 1;
  background: transparent !important;
  height: 100%;
  width: 100%;
}
.rete-flow-canvas__area .minimap {
  bottom: 24px !important;
  left: 24px !important;
  right: auto !important;
}
.rete-flow-canvas__area .rete-flow--dim {
  opacity: 0.22;
  transition: opacity 0.2s;
}
.rete-flow-canvas__area .rete-flow--active {
  opacity: 1;
  transition: opacity 0.2s;
}
.rete-flow-canvas__area .rete-flow--active .rete-flow-connection__line {
  stroke: #7047a4 !important;
  stroke-width: 5px !important;
  filter: drop-shadow(0 0 4px #7047a499);
}
.rete-flow-canvas__area .rete-flow--active .rete-stage-node {
  box-shadow: 0 0 0 2px #7047a4, 0 6px 22px #7047a455;
}
.rete-flow-canvas__area .rete-flow--current .rete-stage-node {
  box-shadow: 0 0 0 4px #e0a100, 0 6px 26px #e0a10066;
}
.rete-flow-canvas__area .rete-flow--search-match {
  z-index: 10;
}
.rete-flow-canvas__area .rete-flow--search-match.rete-flow--current {
  z-index: 11;
}
.rete-flow-canvas__area .rete-flow--search-match:hover,
.rete-flow-canvas__area .rete-flow--search-match:focus-within {
  z-index: 12;
}
.rete-flow-canvas__area .rete-flow--search-match .rete-stage-node {
  border-color: #ef9f2d;
  box-shadow: 0 0 0 2px rgba(239, 159, 45, 0.35),
    0 8px 24px rgba(239, 159, 45, 0.2);
}

.rete-flow-search-details {
  background: #fff8e7;
  border-top: 1px solid #f2dba2;
  border-radius: 0 0 10px 10px;
  color: #473c29;
  padding: 10px 14px;
  font-size: 11px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.rete-flow-search-details > strong,
.rete-flow-search-details small,
.rete-flow-search-details span {
  display: block;
}
.rete-flow-search-details > div {
  margin-top: 8px;
}
.rete-flow-search-details small {
  color: #7d6a45;
  font-size: 10px;
}
.rete-flow-search-details > small {
  margin-top: 8px;
}
.rete-flow-search-details mark {
  background: #ffdf70;
  color: #30283c;
}
.rete-inline-fields__field--search-match {
  position: relative;
}
.rete-inline-fields__field--search-match > input[type="text"],
.rete-inline-fields__field--search-match > textarea {
  color: transparent !important;
  caret-color: #30283c;
}
.rete-flow-search-text-overlay {
  color: #30283c;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}
.rete-flow-search-text-overlay mark {
  background: #ffdf70;
  color: #30283c;
}
::highlight(rete-flow-search-match) {
  background: #ffdf70;
  color: #30283c;
}
.rete-flow-search {
  background: #fff;
  border: 1px solid #e5dced;
  border-radius: 10px;
  box-shadow: 0 8px 30px #29163826;
  left: 50%;
  max-width: calc(100% - 32px);
  padding: 6px;
  position: absolute;
  top: 14px;
  transform: translateX(-50%);
  width: 380px;
  z-index: 5;
}
.rete-flow-search {
  backdrop-filter: blur(6px);
  border-radius: 14px;
  box-shadow: 0 12px 40px #2916384d;
  width: 440px;
}
.rete-flow-search input,
.rete-flow-canvas input,
.rete-flow-canvas textarea {
  user-select: text;
  -webkit-user-select: text;
}
.rete-flow-search input {
  background: #faf9fc
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23827b8e' stroke-width='2.5' stroke-linecap='round'><circle cx='11' cy='11' r='7'/><path d='M21 21l-4.3-4.3'/></svg>")
    no-repeat 12px center;
  border: 1px solid #e5dced;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  padding: 12px 14px 12px 38px;
  width: 100%;
}
.rete-flow-search input:focus {
  border-color: #7047a4;
}
.rete-flow-search ul {
  list-style: none;
  margin: 6px 0 0;
  max-height: 240px;
  overflow-y: auto;
  padding: 0;
}
.rete-flow-search li {
  align-items: center;
  border-radius: 6px;
  color: #51405f;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 8px;
  padding: 8px 10px;
}
.rete-flow-search li.is-active,
.rete-flow-search li:hover {
  background: #f4effa;
  color: #7047a4;
}
.rete-flow-search li strong {
  flex: 1;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rete-flow-search__dot {
  border-radius: 50%;
  height: 10px;
  width: 10px;
}
.rete-flow-search p {
  color: #827b8e;
  font-size: 12px;
  margin: 8px 6px 4px;
}
.rete-flow-canvas__hint {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #ebe7f0;
  border-radius: 8px;
  bottom: 14px;
  color: #827b8e;
  font-size: 10px;
  left: 50%;
  padding: 7px 10px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 2;
}
</style>
